/**
 * Каркас приложения: верхняя панель с навигацией, маршруты экранов, уведомления.
 *
 * Игрок:          /  — лобби или стол;  /profile — профиль;  /profile/games/:id — повтор розыгрыша.
 * Администратор:  /admin — обзор, пользователи, розыгрыши;  /admin/users/:id;  /admin/games/:id.
 * Без входа — экран входа и регистрации; /auth/complete — возврат от провайдера SSO.
 */
import { useEffect, useState } from 'react';
import { t } from '../i18n';
import { cr } from '../lib/format';
import { Link, match, navigate, usePath } from '../lib/router';
import { updateSettings, useSettings } from '../lib/settings';
import { client, useApp } from '../net/client';
import { AdminPage } from './admin/AdminPage';
import { LoginPage, useAuthComplete } from './auth/LoginPage';
import { Avatar } from './kit';
import { LangSwitch } from './LangSwitch';
import { Lobby } from './Lobby';
import { ProfilePage } from './profile/ProfilePage';
import { ReplayPage } from './profile/ReplayPage';
import { Rules } from './Rules';
import { Table } from './Table';

export function App() {
  useAuthComplete();
  const conn = useApp((s) => s.conn);
  const toasts = useApp((s) => s.toasts);
  return (
    <div className="app">
      <TopBar />
      {conn !== 'open' && <ConnBanner />}
      <main>
        <Screen />
      </main>
      <div className="toasts" aria-live="assertive">
        {toasts.map((x) => (
          <div key={x.id} className={`toast ${x.kind}`}>
            {x.text}
          </div>
        ))}
      </div>
    </div>
  );
}

function Screen() {
  const path = usePath();
  const needLogin = useApp((s) => s.needLogin);
  const role = useApp((s) => s.role);
  const profile = useApp((s) => s.profile);
  const room = useApp((s) => s.room);

  // адреса другой роли недоступны: администратор не играет, игрок не видит администрирование
  useEffect(() => {
    if (role === 'admin' && !path.startsWith('/admin')) navigate('/admin', { replace: true });
    if (role === 'player' && path.startsWith('/admin')) navigate('/', { replace: true });
  }, [role, path]);

  if (path === '/auth/complete') return <div className="center dim">{t('app.signingIn')}</div>;
  if (needLogin) return <LoginPage />;
  if (!role) return <div className="center dim">{t('common.loading')}</div>;

  if (role === 'admin') {
    const u = match('/admin/users/:id', path);
    if (u) return <ProfilePage userId={u.id!} />;
    const g = match('/admin/games/:id', path);
    if (g) return <ReplayPage gameId={g.id!} admin />;
    return <AdminPage />;
  }

  if (!profile) return <div className="center dim">{t('common.loading')}</div>;
  const g = match('/profile/games/:id', path);
  const page = path === '/profile' ? <ProfilePage /> : g ? <ReplayPage gameId={g.id!} admin={false} /> : null;
  if (page)
    return (
      <>
        {room && room.meSeated && <BackToTable />}
        {page}
      </>
    );
  return room ? <Table room={room} /> : <Lobby />;
}

function BackToTable() {
  const game = useApp((s) => s.game);
  const active = game && !game.settlement && game.me.participant;
  return (
    <div className={`back-banner ${active ? 'hot' : ''}`}>
      <span>{active ? t('app.activeGame') : t('app.seated')}</span>
      <Link className="btn tiny primary" to="/">
        {t('app.backToTable')}
      </Link>
    </div>
  );
}

function TopBar() {
  const profile = useApp((s) => s.profile);
  const role = useApp((s) => s.role);
  const adminLogin = useApp((s) => s.adminLogin);
  const info = useApp((s) => s.info);
  const needLogin = useApp((s) => s.needLogin);
  const path = usePath();
  const settings = useSettings();
  const [rules, setRules] = useState(false);
  const [menu, setMenu] = useState(false);
  useEffect(() => setMenu(false), [path]);
  const nav =
    role === 'admin'
      ? [{ to: '/admin', title: t('app.nav.admin'), on: path.startsWith('/admin') }]
      : role === 'player'
        ? [
            { to: '/', title: t('app.nav.lobby'), on: path === '/' },
            { to: '/profile', title: t('app.nav.profile'), on: path.startsWith('/profile') },
          ]
        : [];
  return (
    <header className="topbar">
      <Link to={role === 'admin' ? '/admin' : '/'} className="brand">
        {t('app.brand')}
      </Link>
      <span className="demo-badge" title={t('app.demoTitle')}>
        {t('app.demo')}
        <span className="hide-sm"> {t('app.demoMore')}</span>
      </span>
      <nav className="top-nav" aria-label={t('app.nav.label')}>
        {nav.map((n) => (
          <Link key={n.to} to={n.to} className={n.on ? 'on' : ''} aria-current={n.on ? 'page' : undefined}>
            {n.title}
          </Link>
        ))}
      </nav>
      <span className="spacer" />
      {info && (
        <button className="btn ghost tiny hide-sm" onClick={() => setRules(true)}>
          {t('app.rules')}
        </button>
      )}
      <button className="btn ghost tiny icon" onClick={() => updateSettings((s) => ({ ...s, theme: s.theme === 'dark' ? 'light' : 'dark' }))} aria-label={t('app.theme')} title={t('app.theme')}>
        {settings.theme === 'dark' ? '☀' : '☾'}
      </button>
      {/* на экране входа переключатель языка — на самой странице входа */}
      {!needLogin && <LangSwitch className="top-lang" />}
      {profile && (
        <span className="balance-chip mono" title={t('app.balanceTitle')}>
          {cr(profile.balance)}
        </span>
      )}
      {(profile || role === 'admin') && (
        <div className="account">
          <button className="account-btn" onClick={() => setMenu((v) => !v)} aria-expanded={menu} aria-haspopup="menu">
            {profile ? <Avatar id={profile.id} name={profile.name} size={28} /> : <span className="avatar admin">A</span>}
            <span className="hide-sm">{profile?.name ?? adminLogin}</span>
          </button>
          {menu && (
            <div className="account-menu" role="menu">
              {profile && (
                <Link to="/profile" role="menuitem">
                  {t('app.menu.profile')}
                </Link>
              )}
              {info && (
                <button role="menuitem" onClick={() => (setMenu(false), setRules(true))}>
                  {t('app.rules')}
                </button>
              )}
              <button role="menuitem" onClick={() => client.logout()}>
                {t('app.menu.logout')}
              </button>
            </div>
          )}
        </div>
      )}
      {rules && info && <Rules info={info} onClose={() => setRules(false)} />}
    </header>
  );
}

/** Состояние соединения. После нескольких неудач показывает транспорт, адрес и код закрытия. */
function ConnBanner() {
  const conn = useApp((s) => s.conn);
  const ci = useApp((s) => s.connInfo);
  const text = conn === 'connecting' && ci.attempts === 0 ? t('app.conn.connecting') : t('app.conn.lost');
  return (
    <div className="conn-banner">
      {text}
      {ci.attempts >= 2 && (
        <span className="conn-detail">
          {ci.transport === 'ws' ? 'WebSocket' : t('app.conn.poll')} <code>{ci.url}</code>
          {ci.lastCode !== null ? ` ${t('app.conn.code', { code: ci.lastCode })}` : ''}
          {t('app.conn.attempts', { n: ci.attempts })} {ci.transport === 'ws' ? t('app.conn.wsHint') : t('app.conn.pollHint')}
        </span>
      )}
    </div>
  );
}
